import type { Agent } from '@studio/models/agent';
import type { Discover, DiscoveryOptions } from './commands/types.js';
import { PresentationCeiling } from './commands/constants.js';
import {
  classifyDiscoveryCause,
  type DiscoveryCommandResult,
  type DiscoveryFlightState
} from './discovery-outcome.js';

// The raw request owns its deadline/outcome across selector mounts; observers only own timers.
const flights = new Map<
  Agent['id'],
  {
    promise?: Promise<DiscoveryCommandResult> | undefined;
    deadline: number;
    state: DiscoveryFlightState;
  }
>();

export const getDiscoveryFlight = (id: Agent['id']) => {
  const flight = flights.get(id);
  if (flight?.promise && Date.now() >= flight.deadline) flight.state = { kind: 'timeout' };
  return flight;
};

export const collectDiscoveryAgentIds = (
  requestedId: Agent['id'] | undefined,
  brokers: Iterable<{ readonly id: Agent['id'] }>
): Agent['id'][] => {
  const ids = new Set<Agent['id']>();

  if (requestedId) {
    ids.add(requestedId);
  }

  for (const broker of brokers) {
    if (broker.id) {
      ids.add(broker.id);
    }
  }

  return [...ids];
};

export const requestAgentDiscovery = (
  agentId: Agent['id'],
  options: DiscoveryOptions | undefined,
  discover: Discover
): Promise<DiscoveryCommandResult> => {
  const existing = flights.get(agentId);
  if (existing?.promise) return existing.promise;

  const record = {
    deadline: Date.now() + PresentationCeiling,
    state: existing?.state ?? { kind: 'pending' as const },
    promise: undefined as Promise<DiscoveryCommandResult> | undefined
  };
  flights.set(agentId, record);
  // The executor invokes the transport now and captures synchronous throws as well as rejections.
  record.promise = new Promise<DiscoveryCommandResult>((resolve) =>
    resolve(discover(agentId, options))
  )
    .catch(classifyDiscoveryCause)
    .then((result) => {
      record.state = result;
      record.promise = undefined;
      return result;
    });
  return record.promise;
};

export const requestDiscovery = async (
  agentIds: readonly Agent['id'][],
  options: DiscoveryOptions | undefined,
  discover: Discover
): Promise<DiscoveryCommandResult[]> => {
  if (agentIds.length === 0) {
    return [];
  }

  return Promise.all(agentIds.map((agentId) => requestAgentDiscovery(agentId, options, discover)));
};

export const isDiscoveryRequestInFlight = (agentId: Agent['id']): boolean =>
  !!flights.get(agentId)?.promise;

export const resetDiscoveryRequests = (): void => {
  flights.clear();
};
